import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEvent } from 'react-use';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { opTime } from 'utils/dates';
import { OpInfo } from 'new-components/DLS/OpInfo/OpInfo';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { setSelectedVisit } from '../../../../../store/slices/visitsSlice';
import { AppDispatch } from '../../../../../store/store';
import { VisitorSearch } from '../../../../../types/visitorSearchTypes';
import { Visit } from '../../../../../types/visitTypes';

interface ScanQrCodeProps {
  open: boolean;
  visitorsToday: VisitorSearch[];
  onClose: () => void;
  openVisitorsDrawer: () => void;
}

interface QrCodeRecord {
  id: number;
  visitorToken?: string;
  expiresUtc?: string;
}

interface QrCodeResponse {
  data?: QrCodeRecord[] | null;
}

interface SearchRegistrationResponse {
  data?: Visit[] | null;
  filteredCount?: number;
}

interface DescribeVisitResponse {
  data?: Visit[] | null;
}

interface ValidateVisitorRegistrationTokenResponse {
  data?: {
    visitorId?: number;
    data?: {
      visitorId?: number;
      visitId?: number;
      orgId?: number;
    };
  };
}

const getCodeChar = (event: KeyboardEvent): string => {
  if (event.code.startsWith('Key')) {
    return event.code.slice(3).toLowerCase();
  }

  if (event.code.startsWith('Digit')) {
    return event.code.slice(5);
  }

  if (event.code.startsWith('Numpad') && event.code.length === 7) {
    return event.code.slice(6);
  }

  if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
    return '-';
  }

  return '';
};

const ScanQRCodeModal = ({
  open,
  visitorsToday,
  onClose,
  openVisitorsDrawer,
}: ScanQrCodeProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const { orgId } = getOpParams();
  const scannedDataRef = useRef('');
  const isProcessingRef = useRef(false);
  const [scanningInProgress, setScanningInProgress] = useState(false);
  const [isKeyPressed, setIsKeyPressed] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null); // Timer to detect the end of scanning

  useEvent(
    'keydown',
    scanningInProgress
      ? (event: Event) => handleKeyDown(event as KeyboardEvent)
      : null,
    document,
  );

  useEffect(() => {
    if (open) {
      setScanningInProgress(true);
    } else {
      setScanningInProgress(false);
      setIsKeyPressed(false);
      scannedDataRef.current = '';
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [open]);

  const handleScannedData = useCallback(
    async (data: string) => {
      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;

      const scannedValue = data.trim();
      if (!scannedValue) {
        isProcessingRef.current = false;
        onClose();
        return;
      }

      try {
        const normalizedCode = scannedValue.replace(/^s-/i, 'S-');
        const hasQrPrefix = normalizedCode.startsWith('S-');

        let tokenToUse = scannedValue;
        let qrCodeId: number | null = null;

        if (hasQrPrefix) {
          try {
            const response = (await heliumReceive('getQrCode', [orgId], {
              queryStringParams: { code: normalizedCode },
            })) as unknown as QrCodeResponse;
            const qrData = response.data?.[0];
            if (qrData?.visitorToken) {
              if (qrData.expiresUtc) {
                const expirationMs = opTime(qrData.expiresUtc).valueOf();
                if (
                  !Number.isFinite(expirationMs) ||
                  expirationMs <= Date.now()
                ) {
                  notificationService.show(
                    'error',
                    t('scanQrCodeModal.notification.expired.description'),
                  );
                  onClose();
                  return;
                }
              }

              tokenToUse = qrData.visitorToken;
              qrCodeId = qrData.id;
            } else {
              notificationService.show(
                'error',
                t('scanQrCodeModal.notification.invalid.description'),
              );
              onClose();
              return;
            }
          } catch {
            notificationService.show(
              'error',
              t('scanQrCodeModal.notification.invalid.description'),
            );
            onClose();
            return;
          }
        }

        try {
          const response = (await heliumReceive('searchRegistration', [orgId], {
            queryStringParams: { token: tokenToUse },
          })) as unknown as SearchRegistrationResponse;

          const registrationVisit = response.data?.[0];
          const hasRegistration =
            Boolean(registrationVisit) || Number(response.filteredCount) > 0;

          if (!hasRegistration || !registrationVisit) {
            notificationService.show(
              'error',
              t('scanQrCodeModal.notification.scanFail.description'),
            );
            onClose();
            return;
          }

          const registrationVisitorId = registrationVisit.visitors?.[0]?.id;
          const matchedVisitor =
            visitorsToday.find(
              (v) => Number(v.id) === Number(registrationVisitorId),
            ) ??
            visitorsToday.find(
              (v) => Number(v.visitId) === Number(registrationVisit.id),
            );

          if (!matchedVisitor) {
            notificationService.show(
              'error',
              t('scanQrCodeModal.notification.noVisitor.description'),
            );
            onClose();
            return;
          }

          const targetVisitId = matchedVisitor.visitId;
          const targetVisitorId = matchedVisitor.id;

          if (!targetVisitorId) {
            notificationService.show(
              'error',
              t('scanQrCodeModal.notification.noVisitor.description'),
            );
            onClose();
            return;
          }

          const selectedVisit = (await heliumReceive('describeVisit', [
            orgId,
            targetVisitId,
          ])) as unknown as DescribeVisitResponse;
          if (selectedVisit.data?.[0]) {
            dispatch(setSelectedVisit(selectedVisit.data[0]));
          }
          openVisitorsDrawer();
          onClose();

          try {
            const validateResponse = (await heliumSend(
              'validateVisitorTokenByWeb',
              [tokenToUse],
            )) as unknown as ValidateVisitorRegistrationTokenResponse;
            const validatedVisitorId =
              validateResponse?.data?.data?.visitorId ??
              validateResponse?.data?.visitorId ??
              targetVisitorId;

            await heliumSend('insertQrScanLog', [orgId, targetVisitId], {
              visitorId: validatedVisitorId,
              source: 'Web Control Center',
              qrCodeId,
              qrValue: scannedValue,
            });
          } catch {
            notificationService.show(
              'error',
              t('scanQrCodeModal.notification.logFail.description'),
            );
          }
        } catch {
          notificationService.show(
            'error',
            t('scanQrCodeModal.notification.scanFail.description'),
          );
          onClose();
        }
      } finally {
        isProcessingRef.current = false;
      }
    },
    [dispatch, openVisitorsDrawer, onClose, t, orgId, visitorsToday],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsKeyPressed(true);

      if (event.key.length === 1) {
        const isAsciiKey = /^[\x20-\x7E]$/.test(event.key);
        const nextChar = isAsciiKey ? event.key : getCodeChar(event);

        if (nextChar) {
          scannedDataRef.current += nextChar;
        }
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const scannedValue = scannedDataRef.current;
        scannedDataRef.current = '';
        handleScannedData(scannedValue);
      }, 1000);
    },
    [handleScannedData],
  );

  return (
    <OpModal
      title={t('scanQrCodeModal.title')}
      open={open}
      onOk={onClose}
      onCancel={() => {
        scannedDataRef.current = '';
        setScanningInProgress(false);
        setIsKeyPressed(false);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        onClose();
      }}
      okText={t('scanQrCodeModal.confirm')}
      cancelText={t('scanQrCodeModal.cancel')}
      centered
      style={{ textAlign: 'center' }}
      footer={null}
    >
      {scanningInProgress ? (
        <OpSpin
          indicator={<LoadingOutlined spin />}
          size="default"
          style={{ padding: '20px' }}
        />
      ) : undefined}
      {isKeyPressed ? (
        <OpInfo title={t('scanQrCodeModal.alert.processing')} type="success" />
      ) : (
        <OpInfo title={t('scanQrCodeModal.alert.waiting')} type="info" />
      )}
    </OpModal>
  );
};

export default ScanQRCodeModal;
